import React, { memo } from 'react'
import { Chip, Avatar, Typography } from '@mui/material'
import { useTheme, lighten } from '@mui/material/styles'
import UserSkillsCollection from 'components/UserSkillsCollection'
import FocusFadeGroup from 'components/FocusFadeGroup'
import Condition from 'components/Condition'
import { AddCircle, Create } from '@mui/icons-material'
import { graphql, useFragment } from 'react-relay'
import { UserSkillsFields_User$key } from '__generated__/UserSkillsFields_User.graphql'
import { useDataList } from 'hooks/relay'
import { arrayFilterNonNullable } from 'services/array'
import useStyles from './styles'
import UserSkillsEditDialog from './components/UserSkillsEditDialog'

interface IUserSkillsFieldsProps {
  editable: boolean
  user: UserSkillsFields_User$key
}

function UserSkillsFields(props: IUserSkillsFieldsProps) {
  const { classes } = useStyles()
  const { editable, user: userFragmentRef } = props
  const theme = useTheme()

  const user = useFragment(graphql`
    fragment UserSkillsFields_User on User {
      ...UserSkillsEditDialog_User

      profile {
        industry {
          profilesRequireSkills
        }
      }

      userSkills {
        experience
        skill {
          id
          name
        }
      }
    }
  `, userFragmentRef)

  const [isEditMode, setEditMode] = React.useState(false)

  const handleDialogOpen = () => {
    setEditMode(true)
  }

  const handleDialogClose = () => {
    setEditMode(false)
  }

  const skills = useDataList(() => user?.userSkills?.map(us => us.skill ? {
    experience: us.experience,
    skillId: us.skill.id,
    name: us.skill.name,
  } : null)?.filter(arrayFilterNonNullable) || [], [user])
  const hasSkills = skills.length > 0

  let error: string | null = null

  if (user?.profile?.industry?.profilesRequireSkills && !hasSkills && editable) {
    error = 'At least 1 skill is required'
  }

  return (
    <FocusFadeGroup focused={false}>
      <div className={classes.skills} data-cy="skills">
        <div className={classes.skillsWrapper}>
          {(skills.length > 0 || editable) && (
            <UserSkillsCollection
              userSkills={skills}
              animated={false}
              alwaysOpen
            >
              {editable && (
                <div style={{ margin: theme.spacing(0, 1, 1), display: 'inline-block' }}>
                  <Chip
                    data-cy="profile-skills-edit"
                    variant="outlined"
                    label={hasSkills ? 'Edit' : 'Add your skills'}
                    onClick={handleDialogOpen}
                    avatar={(
                      <Avatar style={{ color: lighten(theme.palette.text.dark, 0.25), background: 'none', height: 32, width: 32 }}>
                        {hasSkills ? <Create /> : <AddCircle />}
                      </Avatar>
                    )}
                  />
                </div>
              )}
            </UserSkillsCollection>
          )}

          <Condition condition={Boolean(error)}>
            <Typography variant="body2" className={classes.error} data-cy="profile-skills-error">
              {error}
            </Typography>
          </Condition>
        </div>
      </div>

      {isEditMode && (
        <UserSkillsEditDialog
          open
          onClose={handleDialogClose}
          user={user}
        />
      )}
    </FocusFadeGroup>
  )
}

export default memo(UserSkillsFields)
